import { UI_TAGS } from "@/presentation/helpers/uiTags.js";
import { ACTOR_TAGS } from "@/presentation/helpers/actorTags.js";
import { createGameTextInstruction } from "@/presentation/prefabs/ui/gameTextInstruction.js";
import { executeBotTurn } from "@/ai/botPlayer.js";
import { drawCardFullHand } from "@/presentation/renderers/gameSceneRenderer.js";
import { getActionCardsInHand } from "@/presentation/helpers/selectors.js";

async function handleBotTurn(k, gameState, playableCards) {
    const activePlayer = gameState.getCurrentPlayer();
    const isHandLocked = (playableCards.length === 0);

    // Render the player's hand'
    await drawCardFullHand(k, activePlayer);

    const decision = executeBotTurn(gameState, isHandLocked, playableCards);

    // Robot "thinking"
    await k.wait(1.25);

    if (decision.discard) {
        k.destroyAll(UI_TAGS.TEXT_INSTRUCTION_DISCARD);
        const playerHand = getActionCardsInHand(k);
        const cardActor = playerHand.find(c => c.handIndex === decision.cardIndex);

        gameState.forceDiscard(decision.cardIndex);
        k.destroy(cardActor);

        await k.wait(0.5);
        gameState.drawActionCardManager();
    } else {
        gameState.selectCard(decision.cardIndex);
        await k.wait(1.0, () => gameState.playSelectedCard(decision.slotIndex)); // Temps pour que l'humain voie la sélection
    }
}

async function handleHumanTurn(k, gameState, playableCards) {
    const activePlayer = gameState.getCurrentPlayer();
    const isHandLocked = (playableCards.length === 0);

    // Initial hand render
    await drawCardFullHand(k, activePlayer);

    // Discard instruction or playable card effect
    if (isHandLocked) {
        createGameTextInstruction(k, "Aucune carte jouable !\nCliquez sur une carte pour la défausser.", {
            tag: UI_TAGS.TEXT_INSTRUCTION_DISCARD, isBouncing: true
        });
    } else {
        playableCards.forEach(cardIndex => {
            const cardActor = k.get(ACTOR_TAGS.ACTION_CARD)[cardIndex];
            if (cardActor) cardActor.setIsPlayableEffect(true);
        });
    }

    // Interaction loop
    let turnResolved = false;
    while (!turnResolved) {
        const interaction = await waitForPlayerInteraction(k, gameState);

        if (interaction.type === "CARD_CLICK") {
            if (isHandLocked) {
                k.destroyAll(UI_TAGS.TEXT_INSTRUCTION_DISCARD);
                const cardActor = k.get(ACTOR_TAGS.ACTION_CARD).find(c => c.handIndex === interaction.index);

                gameState.forceDiscard(interaction.index);
                k.destroy(cardActor);
                gameState.drawActionCardManager();
                turnResolved = true; // Resolved
            } else {
                gameState.selectCard(interaction.index);
            }
        }
        else if (interaction.type === "TARGET_CLICK") {
            gameState.playSelectedCard(interaction.slotIndex);
            turnResolved = true; // Resolved
        }
    }
}

function waitForPlayerInteraction(k, gameState) {
    return new Promise((resolve) => {
        const eventHandles = [];

        // Clean up event listeners
        function cleanup() {
            eventHandles.forEach(h => h.cancel());
        }

        // Listen action card click
        k.get(ACTOR_TAGS.ACTION_CARD).forEach(cardActor => {
            const handleCardInteraction = () => {
                cleanup();
                resolve({ type: "CARD_CLICK", index: cardActor.handIndex });
            };

            eventHandles.push(cardActor.onClick(handleCardInteraction));
        });

        // Listen line slot click
        k.get(ACTOR_TAGS.LINE_SLOT).forEach(slotActor => {
            const handleSlotInteraction = () => {
                if (gameState.selectedCardIndex === null) return;

                const activePlayer = gameState.getCurrentPlayer();
                const card = activePlayer.hand[gameState.selectedCardIndex];

                if (card.isValidOn(slotActor.slotIndex, gameState)) {
                    cleanup();
                    resolve({ type: "TARGET_CLICK", slotIndex: slotActor.slotIndex });
                } else {
                    // k.play("error_sound"); // DESIGN ?
                    console.log("Cible invalide");
                }
            };

            eventHandles.push(slotActor.onClick(handleSlotInteraction));
        });
    });
}

export { handleBotTurn, handleHumanTurn };